import type { Course, AppSettings, GPAVariant } from '@gpa-scales/shared-types';
import { AGSubject, GradeLevel, CourseType, ResidencyStatus } from '@gpa-scales/shared-types';
import { resolveGradePoints } from './gradeConversion';
import { isHonorsEligible } from './honorsEligibility';
import { applyRounding } from './rounding';

function isUCEligible(course: Course): boolean {
  return (
    course.agSubject !== AGSubject.None &&
    (course.gradeLevel === GradeLevel.Sophomore || course.gradeLevel === GradeLevel.Junior)
  );
}

function getEligibleCourses(
  courses: Course[],
  settings: AppSettings,
  filter: (course: Course) => boolean
): { course: Course; gradePoints: number }[] {
  const result: { course: Course; gradePoints: number }[] = [];
  for (const course of courses) {
    let points: number | null;
    try {
      points = resolveGradePoints(course, settings);
    } catch {
      continue;
    }
    if (points === null) continue;
    if (!filter(course)) continue;
    result.push({ course, gradePoints: points });
  }
  return result;
}

export function unweightedGPA(courses: Course[], settings: AppSettings): GPAVariant {
  const eligible = getEligibleCourses(courses, settings, () => true);

  if (eligible.length === 0) {
    return {
      label: 'Unweighted GPA',
      value: 0,
      includedCourses: 0,
      totalCourses: courses.length,
      totalCredits: 0,
    };
  }

  let totalPoints = 0;
  let totalCredits = 0;
  for (const { course, gradePoints } of eligible) {
    totalPoints += gradePoints * course.credits;
    totalCredits += course.credits;
  }

  const gpa = totalCredits > 0 ? totalPoints / totalCredits : 0;
  const rounded = settings.roundingRule ? applyRounding(gpa, settings.roundingRule) : gpa;

  return {
    label: 'Unweighted GPA',
    value: rounded,
    includedCourses: eligible.length,
    totalCourses: courses.length,
    totalCredits,
  };
}

export function ucCappedGPA(courses: Course[], settings: AppSettings): GPAVariant {
  const eligible = getEligibleCourses(courses, settings, isUCEligible);

  if (eligible.length === 0) {
    return {
      label: 'UC Capped Weighted GPA',
      value: 0,
      includedCourses: 0,
      totalCourses: courses.length,
      totalCredits: 0,
      isEligible: false,
      threshold: settings.residencyStatus === ResidencyStatus.CaliforniaResident ? 3.0 : 3.4,
    };
  }

  // Determine honors-eligible courses
  const honorsEligible = eligible.filter((e) => isHonorsEligible(e.course, settings));

  // Sort by grade points descending for cap allocation
  const sortedHonors = [...honorsEligible].sort((a, b) => b.gradePoints - a.gradePoints);

  // Apply 10th-grade sub-cap (max 4)
  const tenthGradeHonors = sortedHonors.filter(
    (e) => e.course.gradeLevel === GradeLevel.Sophomore
  );
  const cappedTenth = tenthGradeHonors.slice(0, 4);

  // Apply total cap (max 8)
  const eleventhGradeHonors = sortedHonors.filter(
    (e) => e.course.gradeLevel === GradeLevel.Junior
  );
  const allCapped = [...cappedTenth, ...eleventhGradeHonors].slice(0, 8);

  const cappedCourseIds = new Set(allCapped.map((e) => e.course.id));

  let totalPoints = 0;
  let totalCredits = 0;

  for (const { course, gradePoints } of eligible) {
    const bonus = cappedCourseIds.has(course.id) ? 1.0 : 0;
    totalPoints += (gradePoints + bonus) * course.credits;
    totalCredits += course.credits;
  }

  // UC rule: NO rounding
  const gpa = totalCredits > 0 ? totalPoints / totalCredits : 0;
  const threshold = settings.residencyStatus === ResidencyStatus.CaliforniaResident ? 3.0 : 3.4;

  return {
    label: 'UC Capped Weighted GPA',
    value: gpa,
    includedCourses: eligible.length,
    totalCourses: courses.length,
    totalCredits,
    isEligible: gpa >= threshold,
    threshold,
  };
}

export function ucUncappedGPA(courses: Course[], settings: AppSettings): GPAVariant {
  const eligible = getEligibleCourses(courses, settings, isUCEligible);

  if (eligible.length === 0) {
    return {
      label: 'UC Uncapped Weighted GPA',
      value: 0,
      includedCourses: 0,
      totalCourses: courses.length,
      totalCredits: 0,
    };
  }

  let totalPoints = 0;
  let totalCredits = 0;

  for (const { course, gradePoints } of eligible) {
    const bonus = isHonorsEligible(course, settings) ? 1.0 : 0;
    totalPoints += (gradePoints + bonus) * course.credits;
    totalCredits += course.credits;
  }

  // UC rule: NO rounding
  const gpa = totalCredits > 0 ? totalPoints / totalCredits : 0;

  return {
    label: 'UC Uncapped Weighted GPA',
    value: gpa,
    includedCourses: eligible.length,
    totalCourses: courses.length,
    totalCredits,
  };
}

export function ucUnweightedGPA(courses: Course[], settings: AppSettings): GPAVariant {
  const eligible = getEligibleCourses(courses, settings, isUCEligible);

  if (eligible.length === 0) {
    return {
      label: 'UC Unweighted GPA',
      value: 0,
      includedCourses: 0,
      totalCourses: courses.length,
      totalCredits: 0,
    };
  }

  let totalPoints = 0;
  let totalCredits = 0;

  for (const { course, gradePoints } of eligible) {
    totalPoints += gradePoints * course.credits;
    totalCredits += course.credits;
  }

  // UC rule: NO rounding
  const gpa = totalCredits > 0 ? totalPoints / totalCredits : 0;

  return {
    label: 'UC Unweighted GPA',
    value: gpa,
    includedCourses: eligible.length,
    totalCourses: courses.length,
    totalCredits,
  };
}

export function commonAppGPA(courses: Course[], settings: AppSettings): GPAVariant {
  const eligible = getEligibleCourses(courses, settings, () => true);

  if (eligible.length === 0) {
    return {
      label: 'Common App Weighted GPA',
      value: 0,
      includedCourses: 0,
      totalCourses: courses.length,
      totalCredits: 0,
    };
  }

  let totalPoints = 0;
  let totalCredits = 0;

  for (const { course, gradePoints } of eligible) {
    const isAdvanced = [
      CourseType.AP,
      CourseType.IB_HL,
      CourseType.IB_SL,
      CourseType.Honors,
      CourseType.DualEnrollment,
    ].includes(course.courseType);
    const bonus = isAdvanced ? 1.0 : 0;
    totalPoints += (gradePoints + bonus) * course.credits;
    totalCredits += course.credits;
  }

  const gpa = totalCredits > 0 ? totalPoints / totalCredits : 0;
  const rounded = settings.roundingRule ? applyRounding(gpa, settings.roundingRule) : gpa;

  return {
    label: 'Common App Weighted GPA',
    value: rounded,
    includedCourses: eligible.length,
    totalCourses: courses.length,
    totalCredits,
  };
}

export function calculateAllGPA(courses: Course[], settings: AppSettings): GPAVariant[] {
  return [
    unweightedGPA(courses, settings),
    ucCappedGPA(courses, settings),
    ucUncappedGPA(courses, settings),
    ucUnweightedGPA(courses, settings),
    commonAppGPA(courses, settings),
  ];
}
